import * as React from "react";
import { useSearchParams } from "react-router-dom";

import { PageLayout } from "@/shared/components/PageLayout";
import {
  EphemeralWorkspaceState,
  EphemeralWorkspaceStateContext,
} from "@/shared/components/visualization/WorkspaceCtx/EphemeralWorkspaceState";
import { LayoutRecord, LayoutService } from "@/shared/domain/layouts";
import { SearchParams } from "@/shared/environment";
import { usePopupState } from "@/shared/hooks";
import { useRegisterViewingContext } from "@/shared/services";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { useVizState } from "@/shared/state/visualization";
import { nanoSecToSecStr, strSecToBigIntNanosec } from "@/shared/time";

import { DndContainer } from "../DndContainer";
import { Header } from "../Header";
import { DatasourceInfo } from "../Header/DatasourceInfo";
import { useWorkspaceLoaderData } from "../loader";
import { PanelBoard } from "../PanelBoard";
import { Sidebar } from "../Sidebar";
import { Timeline } from "../Timeline";
import { useWorkspaceState } from "../WorkspaceCtx";

import { useLayoutHydration } from "./hooks/useLayoutHydration";
import { useWorkspaceActionSubscriber } from "./hooks/useWorkspaceActionSubscriber";
import { HydrationError } from "./HydrationError";
import { LayoutConflictModal } from "./LayoutConflictModal";
import { type Notification, Notifications } from "./Notifications";
import { useEnforceStorageQuota } from "./useEnforceStorageQuota";
import styles from "./Workspace.module.css";
import { WorkspaceComment } from "./WorkspaceComment";

export function Workspace() {
  useEnforceStorageQuota();

  // Subscribe to workspace actions from PubSubBus
  useWorkspaceActionSubscriber();

  const visualizerInitData = useWorkspaceLoaderData();
  const workspaceContext = useWorkspaceState();
  const vizState = useVizState();

  useRegisterViewingContext("visualizer", {
    dataSources: workspaceContext.files.map((file) => {
      return {
        type: "file" as const,
        id: file.file_id,
        record: file,
      };
    }),
    state: vizState as unknown as Record<string, unknown>,
  });

  const [workspaceNotifications, setWorkspaceNotifications] = React.useState<
    Notification[]
  >(() => {
    if (visualizerInitData?.type === "recoverable-error") {
      const { name, message } = visualizerInitData.value;

      // Skip layout conflict errors since we have a modal for them
      // TODO disallow magic strings here. There's likely a better way to do this
      if (name === "LayoutConflictError") {
        return [];
      }

      return [
        {
          id: name,
          message: message,
          severity: "error",
        },
      ];
    }
    return [];
  });

  const layoutConflictModalPopup = usePopupState<{
    userLayout: LayoutRecord;
    sharedLayout: LayoutRecord;
  }>();

  useSyncTimerAndSearchParams(workspaceContext);

  const { error: layoutHydrationError } = useLayoutHydration(workspaceContext);

  const onHydrationError = React.useCallback((error: HydrationError) => {
    const notification: Notification = {
      id: error.id,
      message: error.message,
      severity: "error",
    };
    setWorkspaceNotifications((prevNotifications) => {
      if (prevNotifications.find((n) => n.id === error.id)) {
        return prevNotifications;
      }
      return [...prevNotifications, notification];
    });
  }, []);

  React.useEffect(
    function reportHydrationError() {
      const error = layoutHydrationError;
      if (error) {
        onHydrationError(error);
      }
    },
    [layoutHydrationError, onHydrationError],
  );

  const openLayoutConflictModal = layoutConflictModalPopup.open;
  React.useEffect(
    function handleLayoutConflictError() {
      if (
        visualizerInitData?.type === "recoverable-error" &&
        visualizerInitData.value.name === "LayoutConflictError"
      ) {
        try {
          const { userLayout, sharedLayout } =
            LayoutService.parseRobotoLayoutConflictException(
              visualizerInitData.value.metadata?.conflictingLayouts,
            );

          openLayoutConflictModal({
            metadata: { userLayout, sharedLayout },
          });
        } catch (e) {
          // Swallow and report the programming error
          ErrorMonitoringService.captureError(e);
        }
      }
    },
    [openLayoutConflictModal, visualizerInitData],
  );

  return (
    <EphemeralWorkspaceStateContext.Provider value={workspaceContext}>
      <PageLayout
        title={"Roboto - Visualization"}
        headerContentLeft={<DatasourceInfo />}
        headerContentRight={<Header />}
      >
        <div className={styles.workspaceContainer}>
          <Notifications
            onDismiss={(notification) => {
              setWorkspaceNotifications((prevNotifications) =>
                prevNotifications.filter((n) => n.id !== notification.id),
              );
            }}
            notifications={workspaceNotifications}
          />
          <DndContainer>
            <div className={styles.primaryContent}>
              <Sidebar />
              <PanelBoard
                className={styles.panelBoard}
                errorMsg={workspaceContext.error?.message}
              />
            </div>
          </DndContainer>
          <Timeline />
        </div>

        <WorkspaceComment />

        <LayoutConflictModal
          open={layoutConflictModalPopup.isOpen}
          conflictingLayouts={layoutConflictModalPopup.metadata}
          onClose={layoutConflictModalPopup.close}
          onLayoutRenamed={workspaceContext.setActiveLayout}
        />
      </PageLayout>
    </EphemeralWorkspaceStateContext.Provider>
  );
}

/**
 * Synchronizes the given {@param timer} to the URL query string.
 *
 * This works like a database and a cache -- the query parameters are the source of truth
 * for what time to jump the workspace to, and the timer contains a cache where the next
 * state for the time value is stored.
 *
 * When the timer stops or seeks, it commits the working cache to the URL search params.
 *
 * If the value from the URL search params changes, it invalidates the working cache.
 */
function useSyncTimerAndSearchParams({ timer }: EphemeralWorkspaceState) {
  const expectedTimeSearchParam = React.useRef<string>();
  const [searchParams, setSearchParams] = useSearchParams();
  React.useEffect(
    function onSyncTimerAndSearchParams() {
      function syncTimerToParams() {
        // Sync the query params to the timer
        const timestamp = searchParams.get(SearchParams.TIMESTAMP) ?? undefined;
        // Only sync timer state after the timer has data.
        if (timer.duration <= 0) {
          return;
        }

        // Sync the timer to query params when and only when the
        // query params have been updated independently of the timer.
        if (
          timestamp !== undefined &&
          // Don't apply this update if it has already been applied.
          timestamp !== expectedTimeSearchParam.current
        ) {
          timer.seekTo(strSecToBigIntNanosec(timestamp));
          expectedTimeSearchParam.current = timestamp;
        }
      }

      function syncParamsToTimer() {
        // Sync the query params to the timer
        const timestamp = nanoSecToSecStr(timer.currentTime);
        // Only sync timer state after the timer has data.
        if (timer.duration <= 0) {
          return;
        }

        // Short-circuit the feedback loop from the timer to the query parameters.
        expectedTimeSearchParam.current = timestamp;
        // Avoid an unnecessary params update if possible.
        if (searchParams.get(SearchParams.TIMESTAMP) !== timestamp) {
          setSearchParams(
            (oldParams) => {
              const params = new URLSearchParams(oldParams);
              params.set(SearchParams.TIMESTAMP, timestamp);
              return params;
            },
            { preventScrollReset: true, replace: true },
          );
        }
      }
      // If the query params or timer changed, then sync them together now.
      syncTimerToParams();

      // Sync again when the timer's internal time span state changes.
      const abortController = new AbortController();
      const listenerOptions = { signal: abortController.signal };
      // timeSpanUpdate means that the timer's duration and start time may have both changed.
      timer.addListener("timeSpanUpdate", syncTimerToParams, listenerOptions);

      // When the timer stops on a specific time, sync the query params to the timer.
      timer.addListener("stop", syncParamsToTimer, listenerOptions);
      timer.addListener("seek", syncParamsToTimer, listenerOptions);
      return function disposeOnSyncTimer() {
        abortController.abort();
      };
    },
    [timer, setSearchParams, searchParams],
  );
}
